/**
 * Menu di una riga della lista chat (tasto destro o pulsante "..."):
 * letta/non letta, ricontatta, fissa in alto, archivia, silenzia. In un portale sopra tutto,
 * tenuto dentro lo schermo; si chiude con Escape e col clic fuori.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Eye, EyeOff, Pin, PinOff, Archive, ArchiveRestore, BellOff, Bell, BellRing } from 'lucide-react';
import type { MuteOption } from '@/lib/services/whatsapp/chatListRules';

export interface ChatRowMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  /** undefined = voce letta/non letta assente (es. canali senza questa funzione). */
  hasUnread?: boolean;
  onMarkAsRead?: () => void;
  onMarkAsUnread?: () => void;
  pinned: boolean;
  archived: boolean;
  muted: boolean;
  /** false se il DB non ha ancora le colonne (migration da applicare): voci disattivate. */
  prefsAvailable: boolean;
  onTogglePin: () => void;
  onToggleArchive: () => void;
  onMute: (option: MuteOption | null) => void;
  /** Apre le scelte "Ricontatta" (assente = voce nascosta). */
  onRecontact?: () => void;
  hasRecontact?: boolean;
}

const itemCls = 'w-full px-3 py-2 text-left text-sm text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed';

export function ChatRowMenu(p: ChatRowMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: p.x, top: p.y });
  const [muteOpen, setMuteOpen] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      left: Math.max(8, Math.min(p.x, window.innerWidth - r.width - 8)),
      top: Math.max(8, Math.min(p.y, window.innerHeight - r.height - 8)),
    });
  }, [p.x, p.y, muteOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); p.onClose(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [p]);

  const run = (fn?: () => void) => () => { fn?.(); p.onClose(); };
  const off = !p.prefsAvailable;

  return createPortal(
    <>
      <div className="fixed inset-0" style={{ zIndex: 9999 }} onClick={p.onClose} onContextMenu={(e) => { e.preventDefault(); p.onClose(); }} />
      <div ref={ref} role="menu" className="fixed min-w-[210px] py-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.09)] dark:border-white/10" style={{ zIndex: 9999, left: pos.left, top: pos.top }}>
        {p.hasUnread !== undefined && (p.hasUnread ? (
          <button type="button" role="menuitem" className={itemCls} onClick={run(p.onMarkAsRead)}><Eye className="w-4 h-4 text-[#787774]" />Segna come letta</button>
        ) : (
          <button type="button" role="menuitem" className={itemCls} onClick={run(p.onMarkAsUnread)}><EyeOff className="w-4 h-4 text-[#787774]" />Segna come non letta</button>
        ))}
        {p.onRecontact && (
          <button type="button" role="menuitem" className={itemCls} onClick={run(p.onRecontact)}>
            <BellRing className="w-4 h-4 text-[#D9730D]" />{p.hasRecontact ? 'Rimanda il ricontatto...' : 'Ricontatta...'}
          </button>
        )}
        <button type="button" role="menuitem" className={itemCls} disabled={off} onClick={run(p.onTogglePin)}>
          {p.pinned ? <PinOff className="w-4 h-4 text-[#787774]" /> : <Pin className="w-4 h-4 text-[#787774]" />}
          {p.pinned ? 'Togli da in alto' : 'Fissa in alto'}
        </button>
        <button type="button" role="menuitem" className={itemCls} disabled={off} onClick={run(p.onToggleArchive)}>
          {p.archived ? <ArchiveRestore className="w-4 h-4 text-[#787774]" /> : <Archive className="w-4 h-4 text-[#787774]" />}
          {p.archived ? 'Togli dall\'archivio' : 'Archivia'}
        </button>
        {p.muted ? (
          <button type="button" role="menuitem" className={itemCls} disabled={off} onClick={run(() => p.onMute(null))}><Bell className="w-4 h-4 text-[#787774]" />Riattiva notifiche</button>
        ) : (
          <button type="button" role="menuitem" aria-expanded={muteOpen} className={itemCls} disabled={off} onClick={() => setMuteOpen((v) => !v)}><BellOff className="w-4 h-4 text-[#787774]" />Silenzia...</button>
        )}
        {muteOpen && !p.muted && (
          <div className="pl-7 pb-1">
            {([['8h', 'Per 8 ore'], ['1w', 'Per 1 settimana'], ['always', 'Sempre']] as const).map(([opt, label]) => (
              <button key={opt} type="button" role="menuitem" className={`${itemCls} py-1.5 text-[13px]`} onClick={run(() => p.onMute(opt))}>{label}</button>
            ))}
          </div>
        )}
        {off && <p className="px-3 pt-1 pb-1.5 text-[11px] text-[#787774] max-w-[230px]">Fissa, archivia e silenzia saranno disponibili a breve.</p>}
      </div>
    </>,
    document.body,
  );
}
